import React, { Component, createRef } from 'react';
import cn from 'classnames';
import noop from 'lodash/noop';
import { FormField } from '@creditflowresearch/uilib/Form/FormField';
import { FormError } from '@creditflowresearch/uilib/Form/FormError';
import { InputLabel } from '@creditflowresearch/uilib/Input/Label';
import { IconComponentType } from '@creditflowresearch/uilib/Icon';
import InputAppend from './InputAppend';
import InputPrepend from './InputPrepend';
import type { InputSize } from './types';
import styles from './input.module.scss';

interface AnyObject {
  [key: string]: any,
}
export interface FieldRenderProps<
  FieldValue,
  T extends HTMLElement = HTMLElement,
  InputValue = FieldValue,
> {
  input: FieldInputProps<InputValue, T>,
  meta: FieldMetaState<FieldValue>,
  [otherProp: string]: any,
}

interface FieldInputProps<FieldValue, T extends HTMLElement = HTMLElement>
  extends AnyObject {
  name: string,
  onBlur: (event?: React.FocusEvent<T>) => void,
  onChange: (event: React.ChangeEvent<T> | any) => void,
  onFocus: (event?: React.FocusEvent<T>) => void,
  type?: string,
  value: FieldValue,
  checked?: boolean,
  multiple?: boolean,
}

export type FieldMetaState<FieldValue> = Pick<FieldState<FieldValue>,
  Exclude<keyof FieldState<FieldValue>, 'blur' | 'change' | 'focus' | 'name' | 'value'>>;

export interface FieldState<FieldValue> {
  active?: boolean,
  blur: () => void,
  change: (value: FieldValue | undefined) => void,
  data?: AnyObject,
  dirty?: boolean,
  dirtySinceLastSubmit?: boolean,
  error?: any,
  focus: () => void,
  initial?: FieldValue,
  invalid?: boolean,
  length?: number,
  modified?: boolean,
  modifiedSinceLastSubmit?: boolean,
  name: string,
  pristine?: boolean,
  submitError?: any,
  submitFailed?: boolean,
  submitSucceeded?: boolean,
  submitting?: boolean,
  touched?: boolean,
  valid?: boolean,
  validating?: boolean,
  value?: FieldValue,
  visited?: boolean,
}

export interface IFinalFormFieldInputComponentProps<FieldValue = any> extends FieldRenderProps<FieldValue> {
  [otherProp: string]: any,
}

export interface IProps extends IFinalFormFieldInputComponentProps {
  name: string,
  type?: 'number' | 'text' | 'date' | 'time' | 'email' | 'password' | 'hidden',
  label?: React.ReactNode,
  placeholder?: string,
  value?: string | number,
  className?: string,
  inputClassName?: string,
  iconPosition?: 'left' | 'right',
  icon?: IconComponentType,
  iconType?: never,
  size?: InputSize,
  minLength?: number,
  maxLength?: number,
  iconClassName?: string,
  disabled?: boolean,
  isReadOnly?: boolean,
  isNarrow?: boolean,
  isErrorMessage?: boolean,
  isClearable?: boolean,
  autoFocus?: boolean,
  onBlur?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  onClick?: React.MouseEventHandler,
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  onFocus?: (e: React.ChangeEvent<HTMLInputElement>) => void,
  onKeyPress?: React.KeyboardEventHandler,
  prependComponent?: React.ReactElement | React.ElementType | React.FC<any>,
  appendComponent?: React.ReactElement | React.ElementType | React.FC<any>,
  isLastPassAutofillEnabled?: boolean,
  formFieldClassName?: string,
  dataTest: string,
  autoCompleteInput?: boolean,
}

interface IState {
  isFocused: boolean,
  isHovered: boolean,
}

const defaultProps: Partial<IProps> = {
  type: 'text',
  iconPosition: 'left',
  disabled: false,
  isReadOnly: false,
  isNarrow: false,
  isErrorMessage: true,
  isClearable: true,
  autoFocus: false,
  value: '',
  size: 'M',
  isLastPassAutofillEnabled: false,
  onBlur: noop,
  onClick: noop,
  onChange: noop,
  onFocus: noop,
  onKeyPress: noop,
  autoCompleteInput: false,
};

class Input extends Component<IProps, IState> {
  static defaultProps: Partial<IProps> = defaultProps;

  constructor(props: IProps) {
    super(props);

    this.state = {
      isFocused: false,
      isHovered: false,
    };

    this.handleOnChange = this.handleOnChange.bind(this);
    this.value = this.value.bind(this);

    this.nodeInput = createRef();
  }

  private nodeInput: React.RefObject<HTMLInputElement>;

  /**
   * Can be called by ref.focus()
   */
  focus = (event?: React.FocusEvent<HTMLInputElement>) => {
    this.setState({
      isFocused: true,
    });

    if (this.props.input && ('onFocus' in this.props.input)) {
      return this.props.input.onFocus(event);
    }
    if (this.nodeInput.current) {
      this.nodeInput.current.focus();
    }

    return this.props.onFocus(event);
  };

  onBlur = (event?: React.FocusEvent<HTMLInputElement>) => {
    if (!this.state.isHovered) {
      this.setState({
        isFocused: false,
      });
    }

    if (this.props.input && ('onBlur' in this.props.input)) {
      return this.props.input.onBlur(event);
    }

    return this.props.onBlur(event);
  };

  value() {
    if (this.props.input && ('value' in this.props.input)) {
      return this.props.input.value ?? '';
    }
    return this.props.value ?? '';
  }

  handleHoverOn = () => {
    this.setState({ isHovered: true });
  };

  handleHoverOff = () => {
    this.setState({ isHovered: false });
  };

  handleOnChange = (event: React.ChangeEvent<HTMLInputElement> | string) => {
    if (this.props.input && ('onChange' in this.props.input)) {
      if (this.props.autoCompleteInput) {
        return this.props.input.onChange(event || { target: { value: '' } });
      }

      return this.props.input.onChange(event);
    }

    return this.props.onChange({
      // @ts-ignore
      target: {
        value: ((event instanceof Object) ? event.target.value : event),
      },
    });
  };

  clear = () => {
    this.handleOnChange('');
    this.nodeInput.current?.focus();
  };

  renderPrependComponent = () => {
    const {
      prependComponent,
      icon,
      iconPosition,
      iconClassName,
      size,
    } = this.props;
    const isCustomIconVisible = Boolean(((iconPosition === 'left') && icon));

    if (prependComponent || isCustomIconVisible) {
      return (
        <InputPrepend
          prependComponent={ prependComponent }
          icon={ icon }
          isCustomIconVisible={ isCustomIconVisible }
          iconClassName={ iconClassName }
          size={ size }
        />
      );
    }

    return null;
  };

  // TODO: use shared hook useCombinedRef instead when component will be function component
  combineRefs = (refs) => (value) => {
    refs.forEach((ref) => {
      if (typeof ref === 'function') {
        ref(value);
      } else if (ref != null) {
        ref.current = value;
      }
    });
  };

  renderAppendComponent = () => {
    const {
      appendComponent,
      isClearable,
      icon,
      iconPosition,
      iconClassName,
      size,
      disabled,
    } = this.props;

    const isCustomIconVisible = Boolean(((iconPosition === 'right') && icon));

    if (appendComponent || isCustomIconVisible || isClearable) {
      const isClearButtonVisible =
        !disabled && isClearable && (this.state.isHovered || this.state.isFocused) && this.value().length > 0;

      return (
        <InputAppend
          clear={ this.clear }
          appendComponent={ appendComponent }
          icon={ icon }
          isCustomIconVisible={ isCustomIconVisible }
          iconClassName={ iconClassName }
          size={ size }
          isClearIconVisible={ isClearButtonVisible }
        />
      );
    }

    return null;
  };

  /**
   * @return {ReactElement}
   */
  render() {
    let {
      input,
      meta,
      type,
      name,
      label,
      placeholder,
      className,
      inputClassName,
      icon: Icon,
      prependComponent: PrependComponent,
      appendComponent: AppendComponent,
      iconPosition,
      isNarrow,
      isErrorMessage,
      isClearable,
      disabled,
      isReadOnly,
      autoFocus,
      maxLength,
      minLength,
      size,
      isLastPassAutofillEnabled,
      onClick,
      onBlur,
      onKeyPress,
      formFieldClassName,
      dataTest,
    } = this.props;
    const { isHovered, isFocused } = this.state;
    const value = this.value();
    const isClearIconVisible = isClearable && value && !disabled && (isHovered || isFocused);

    const inputWrapperCls = cn(styles.formInputWrp, {
      [styles.isPrependComponent]: PrependComponent || ((iconPosition === 'left') && Icon),
      [styles.isAppendComponent]: AppendComponent || ((iconPosition === 'right') && Icon) || isClearIconVisible,
      [styles.isInputIconDisabled]: Icon && disabled,
    }, className);

    const inputCls = cn(styles.formInput, size && styles[`size${ size }`], inputClassName, {
      [styles.isError]: (meta && meta.touched && (meta.error || (!meta.modifiedSinceLastSubmit && meta.submitError))),
      // @ts-ignore
      [styles.isWarning]: meta && meta.touched && meta.warning,
      [styles.isClearable]: isClearIconVisible,
    });

    const inputProps = {
      onClick,
      onBlur, // is overrided by onBlur passed in input
      onKeyPress,
      ...input,
    };

    return (
      <FormField
        isNarrow={ isNarrow }
        isValidationFeedback={
          !!(
            meta &&
            meta.touched &&
            // @ts-ignore
            (meta.error || meta.warning || (!meta.modifiedSinceLastSubmit && meta.submitError))
          )
        }
        dataTest={ dataTest }
        className={ formFieldClassName }
      >
        { label && (
          <InputLabel htmlFor={ input.name }>
            { label }
          </InputLabel>
        ) }
        <label
          className={ inputWrapperCls }
          onMouseOver={ this.handleHoverOn }
          onMouseLeave={ this.handleHoverOff }
        >
          { this.renderPrependComponent() }
          <input
            key="input"
            name={ name }
            type={ type }
            disabled={ disabled }
            placeholder={ placeholder }
            data-lpignore={ !isLastPassAutofillEnabled }
            data-test={ name || inputProps.name }
            autoFocus={ autoFocus }
            maxLength={ maxLength }
            minLength={ minLength }
            readOnly={ isReadOnly }
            value={ this.value() }
            { ...inputProps }
            className={ inputCls }
            onChange={ this.handleOnChange }
            onFocus={ this.focus }
            onBlur={ this.onBlur }
            // @ts-ignore
            ref={ this.combineRefs([this.nodeInput, inputProps.ref]) }
          />
          { this.renderAppendComponent() }
          { isErrorMessage && <FormError { ...meta } /> }
        </label>
      </FormField>
    );
  }
}

export default Input;
